export const DASHBOARD_LAYOUT_AREA = 'dashboard'

export const DEFAULT_DASHBOARD_WIDGETS = [
    { key: 'kpi_sales_today', label: 'Sales Today', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'salesOrders' },
    { key: 'kpi_open_orders', label: 'Open Orders', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'salesOrders' },
    { key: 'kpi_unpaid_ar', label: 'Unpaid AR', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'ar' },
    { key: 'kpi_inventory_value', label: 'Inventory Value', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'products' },
    { key: 'kpi_low_stock', label: 'Low Stock', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'products' },
    { key: 'kpi_open_pos', label: 'Open POs', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'bills' },
    { key: 'kpi_payments_mtd', label: 'Payments MTD', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'payments' },
    { key: 'kpi_total_ar', label: 'Total AR', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'customers' },
    { key: 'kpi_open_picks', label: 'Open Picks', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'batchPick' },
    { key: 'kpi_pending_signups', label: 'Pending Signups', group: 'overview', col: { cols: '12', sm: '6', lg: '4', xl: true }, permission: 'customerReview' },

    { key: 'sales_closed_rankings', label: 'Closed Invoices', group: 'sales', col: { cols: '12', md: '6', className: 'dashboard-col-fifth' }, permission: null },
    { key: 'sales_unpaid_rankings', label: 'Unpaid Invoices', group: 'sales', col: { cols: '12', md: '6', className: 'dashboard-col-fifth' }, permission: null },
    { key: 'sales_aging_rankings', label: 'Aging A/R 30+ Days', group: 'sales', col: { cols: '12', md: '6', className: 'dashboard-col-fifth' }, permission: null },
    { key: 'sales_last_week_rankings', label: 'Last Week', group: 'sales', col: { cols: '12', md: '6', className: 'dashboard-col-fifth' }, permission: null, insertAfter: 'sales_aging_rankings' },
    { key: 'sales_last_month_rankings', label: 'Last Month', group: 'sales', col: { cols: '12', md: '6', className: 'dashboard-col-fifth' }, permission: null, insertAfter: 'sales_last_week_rankings' },

    { key: 'sales_closed_pie', label: 'Closed Invoices Chart', group: 'sales', col: { cols: '12', lg: '4' }, permission: null },
    { key: 'sales_unpaid_pie', label: 'Unpaid Invoices Chart', group: 'sales', col: { cols: '12', lg: '4' }, permission: null },
    { key: 'sales_aging_pie', label: 'Aging A/R Chart', group: 'sales', col: { cols: '12', lg: '4' }, permission: null },
    { key: 'sales_top_products', label: 'Top Products (after discount)', group: 'sales', col: { cols: '12', lg: '6' }, permission: null },
    { key: 'sales_top_customers', label: 'Top Customers', group: 'sales', col: { cols: '12', lg: '6' }, permission: null },

    { key: 'velocity_order_growth', label: 'Customer Order Growth', group: 'velocity', col: { cols: '12', lg: '4' }, permission: null },
    { key: 'velocity_lapse_ratio', label: 'Lapse Ratio', group: 'velocity', col: { cols: '12', lg: '4' }, permission: null },
    { key: 'velocity_sku_breadth', label: 'SKU Breadth', group: 'velocity', col: { cols: '12', lg: '4' }, permission: null },
]

export const DASHBOARD_GROUP_LABELS = {
    overview: 'Overview',
    sales: 'Sales',
    velocity: 'Sales Velocity',
}

export function createDefaultLayout(allowedKeys = null) {
    return DEFAULT_DASHBOARD_WIDGETS
        .filter((widget) => !allowedKeys || allowedKeys.has(widget.key))
        .map((widget) => ({
            key: widget.key,
            visible: 1,
            collapsed: 0,
        }))
}

export function mergeDashboardLayout(savedLayout, allowedKeys = null) {
    const defaults = createDefaultLayout(allowedKeys)
    const defaultByKey = {}
    defaults.forEach((item) => {
        defaultByKey[item.key] = item
    })

    if (!savedLayout || !Array.isArray(savedLayout) || !savedLayout.length) {
        return defaults
    }

    const merged = []
    const seen = new Set()

    savedLayout.forEach((saved) => {
        if (!saved || !saved.key || !defaultByKey[saved.key]) {
            return
        }

        seen.add(saved.key)
        merged.push({
            key: saved.key,
            visible: saved.visible != null ? parseInt(saved.visible, 10) : 1,
            collapsed: saved.collapsed != null ? parseInt(saved.collapsed, 10) : 0,
        })
    })

    defaults.forEach((item) => {
        if (seen.has(item.key)) {
            return
        }

        const meta = getWidgetMeta(item.key)
        if (!insertWidgetRelative(merged, { ...item }, meta)) {
            merged.push({ ...item })
        }
    })

    relocateMisplacedPeriodRankings(merged)

    return merged
}

function insertWidgetRelative(layout, item, meta) {
    if (meta && meta.insertAfter) {
        const anchorIndex = layout.findIndex((row) => row.key === meta.insertAfter)
        if (anchorIndex >= 0) {
            layout.splice(anchorIndex + 1, 0, item)
            return true
        }
    }

    if (meta && meta.insertBefore) {
        const anchorIndex = layout.findIndex((row) => row.key === meta.insertBefore)
        if (anchorIndex >= 0) {
            layout.splice(anchorIndex, 0, item)
            return true
        }
    }

    return false
}

function relocateMisplacedPeriodRankings(layout) {
    const closedIndex = layout.findIndex((item) => item.key === 'sales_closed_rankings')
    const periodKeys = ['sales_last_week_rankings', 'sales_last_month_rankings']
    const misplaced = periodKeys.some((key) => {
        const index = layout.findIndex((item) => item.key === key)
        return index >= 0 && closedIndex >= 0 && index < closedIndex
    })

    if (!misplaced) {
        return
    }

    const moved = []
    periodKeys.forEach((key) => {
        const index = layout.findIndex((item) => item.key === key)
        if (index >= 0) {
            moved.push(layout.splice(index, 1)[0])
        }
    })

    const agingIndex = layout.findIndex((item) => item.key === 'sales_aging_rankings')
    const insertAt = agingIndex >= 0 ? agingIndex + 1 : layout.length
    layout.splice(insertAt, 0, ...moved)
}

export function moveWidget(layout, fromKey, toKey) {
    const fromIndex = layout.findIndex((item) => item.key === fromKey)
    const toIndex = layout.findIndex((item) => item.key === toKey)

    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        return layout
    }

    const next = layout.slice()
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)
    return next
}

export function getWidgetMeta(key) {
    return DEFAULT_DASHBOARD_WIDGETS.find((widget) => widget.key === key) || null
}
